import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ChallengesApi } from '../API/ChallengesApi';
import { useAuth } from '../Auth/AuthProvider';
import { Challenge, ChallengeContextType } from '../Types/Challenge';

const ChallengeContext = createContext<ChallengeContextType | undefined>(
  undefined,
);

export const useChallenges = () => {
  const context = useContext(ChallengeContext);
  if (!context) {
    throw new Error('useChallenges must be used within a ChallengeProvider');
  }
  return context;
};

export const ChallengeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const challengesApi = new ChallengesApi();

  const fetchChallenges = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await challengesApi.getUserChallenges();
      setChallenges(data);
    } catch (err) {
      console.error('Failed to fetch challenges:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch challenges'),
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchChallenges();
    } else {
      setIsLoading(false);
      setChallenges([]);
    }
  }, [isAuthenticated, fetchChallenges]);

  const value = {
    challenges,
    refreshChallenges: fetchChallenges,
  };

  // Don't render anything while loading or on error, let the parent component handle these states
  if (isLoading || error) {
    return null;
  }

  return (
    <ChallengeContext.Provider value={value}>
      {children}
    </ChallengeContext.Provider>
  );
};
